// The FIN payments-desk route subtree — the LIBRARY-scoped verification queue
// (fin-design §Portals STAFF). Code-based routes per DEV-GUIDE-002 §4 — a
// package-composed subtree hand-writes `.lazy()` + `createLazyRoute`. This
// factory hangs `/payments` off the portal's root route so router.tsx stays the
// single wiring point (composed ALONGSIDE the LIB desk subtree — neither
// touches the other).
//
// GATING is per-screen (router.tsx's documented choice): the layout route
// mirrors the SERVICE's module-scoped verifier rule for module 'lib' —
// fin:verify OR lib:desk OR lib:admin (allowedToVerify in modules/fin) — via
// ConsoleLayout's any-of perm array. The UI can only HIDE; the gateway is the
// authority (ADR-001 §14.2): every /api/fin/verification read re-derives the
// caller's verifier scope server-side (empty scope ⇒ 403 FIN_VERIFIER_FORBIDDEN)
// and verify/decline re-check the OR-tier in-service. Anonymous → /login;
// signed-in without any verifier standing → an explicit denied panel, never a
// blank Outlet.
import { Outlet, createRoute } from "@tanstack/solid-router";
import { lazyRoute } from "@erp/ui/route-kit";
import type { Permission } from "@erp/auth-kit/rbac";
import { finQueries } from "../index.js";
import { VerificationTab, type VerificationListQuery } from "../../schemas/index.js";
import { ConsoleLayout } from "@erp/ui/console-layout";
import { panelSearch, type PanelSearch } from "@erp/ui/side-panel";
import type { AnyRoute } from "@tanstack/solid-router";

/* ───────────────────────── URL-as-state (§4.1) ─────────────────────────
 * The checker queue's ?tab facet + omnibox ?search live in the URL.
 * Self-healing per the guide's ideal: the imported VerificationTab Zod enum
 * drives `.optional().catch(undefined)` (the cms.tsx precedent — no direct `z`
 * import; the schema object the API validates with also heals the URL, so UI
 * and gateway can never disagree). A garbage `?tab=` heals to the default
 * rather than throwing.
 *
 * NO ?cursor (2026-09-22). The queue STREAMS: pages accumulate in one
 * `useInfiniteQuery` entry, so a cursor is a position inside the stream rather
 * than a view of it, and it has left the URL, the deps and the loader alike. A
 * stale `?cursor=` on an old bookmark falls out of this validator and reaches
 * neither the stream's filter object nor its first page — the desk opens at
 * the newest claim with the tab, the search and the panel restored. */
export interface PaymentsSearch extends PanelSearch {
  tab?: VerificationTab;
  search?: string;
}

/**
 * The closed panel set for the checker queue. The PROOF VIEWER is a view, so it
 * lives in the URL (`?panel=proofs&panelId=<paymentId>`): a checker can paste a
 * link straight to a claim's evidence, Back closes it, and the queue underneath
 * keeps its tab and search. Verify/decline stay MODALS — they are
 * confirmations, not views.
 */
export const PAYMENTS_PANELS = ["proofs"] as const;

export const PAYMENTS_DEFAULT_TAB: VerificationTab = "pending";
/** Fixed keyset page size — one page of the stream, not one page on screen. */
export const PAYMENTS_PAGE_SIZE = 25;
/**
 * The desk is the LIBRARY payments queue — the module filter is pinned, not a
 * URL facet. The service intersects it with the caller's verifier scope, so a
 * lib:desk assistant and a fin:verify admin both see exactly module='lib' here
 * (a caller with NO standing for 'lib' gets 403 FIN_VERIFIER_FORBIDDEN).
 */
export const PAYMENTS_MODULE = "lib";

const tabParam = VerificationTab.optional().catch(undefined);

function optionalString(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

export function validatePaymentsSearch(input: Record<string, unknown>): PaymentsSearch {
  return {
    tab: tabParam.parse(input.tab),
    search: optionalString(input.search),
    ...panelSearch(PAYMENTS_PANELS, input),
  };
}

/**
 * ONE filters definition serves loader + screen + tests (§4.3 Do) — both sides
 * MUST hit the same cache entry, so an absent tab heals to the default HERE.
 *
 * No `cursor` in the shape at all (2026-09-22): this object is the stream's
 * IDENTITY, and a position inside a stream is not part of what identifies it.
 */
export function paymentsListFilters(s: PaymentsSearch): Omit<VerificationListQuery, "cursor"> {
  return {
    limit: PAYMENTS_PAGE_SIZE,
    tab: s.tab ?? PAYMENTS_DEFAULT_TAB,
    module: PAYMENTS_MODULE,
    search: s.search,
  };
}

/** The screen-gate mirror of modules/fin's allowedToVerify for module 'lib' (any-of). */
export const PAYMENTS_DESK_PERMS: readonly Permission[] = ["fin:verify", "lib:desk", "lib:admin"];

/** The payments-desk shell: gates on the lib verifier tier, then renders the queue. */
function PaymentsDeskLayout() {
  return (
    <ConsoleLayout
      perm={PAYMENTS_DESK_PERMS}
      deniedMessage="This account cannot use the payments desk. Ask an administrator for payments-desk access."
      skeletonId="payments-skeleton"
      deniedId="payments-denied"
      unreachableId="payments-unreachable"
    >
      <Outlet />
    </ConsoleLayout>
  );
}

/**
 * Build the `/payments` subtree under the portal's root route. Returns the
 * routes to spread into `rootRoute.addChildren([...])` (router.tsx). The screen
 * closes its own `.lazy()` loop with `createLazyRoute('/payments/')`.
 */
export function makePaymentsDeskRoutes<TParent extends AnyRoute>(rootRoute: TParent) {
  const paymentsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "payments",
    component: PaymentsDeskLayout,
  });

  const queueRoute = createRoute({
    getParentRoute: () => paymentsRoute,
    path: "/",
    validateSearch: validatePaymentsSearch, // self-healing (§4.1)
    loaderDeps: ({ search }) => ({ tab: search.tab, search: search.search }),
    // Warm the SAME cache entries the screen re-keys — WITHOUT holding the route
    // (audit UX-01; the rationale is on the admin index route). prefetchQuery
    // never throws, so a 401/403 here cannot abort navigation either: the
    // layout's gate renders /login or the denied panel, the screen its ErrorCard.
    // The FIRST PAGE of the stream only; every page after it is fetched by the
    // tail's sentinel.
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchInfiniteQuery(finQueries.verificationListInfinite(paymentsListFilters(deps)));
      void context.queryClient.prefetchQuery(finQueries.verificationStats());
    },
  }).lazy(lazyRoute(() => import("../screens/staff/PaymentsQueue.js")));

  return [paymentsRoute.addChildren([queueRoute])];
}
